/**
 * Baremetal GPU Marketplace Page
 * Displays available GPU inventory from the marketplace service
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CpuChipIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  FunnelIcon,
  ServerIcon,
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import Header from '../../components/layout/Header';
import { marketplaceApi } from '../../services/marketplaceApi';
import { currencyService } from '../../services/api';
import { InventoryResponse, InventoryFilter } from '../../types/marketplace';
import ReservationModal from '../../components/marketplace/ReservationModal';

// Available GPU types for filtering
const GPU_TYPES = [
  'B300', 'B200', 'GB200', 'GB300', 'H200', 'H100',
  'A100', 'L40S', 'V100', 'RTX4090', 'RTX5090', 'RTX3090'
];

// Filter out network-related tags that shouldn't be displayed
const filterFeatures = (features: string[]) => {
  const excludedTags = ['Public', 'NAT', 'Private', 'VPC Capable', 'TEE Capable'];
  return features.filter(f => !excludedTags.includes(f));
};

const Marketplace: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<InventoryFilter>({
    page: 1,
    pageSize: 12,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGpuTypes, setSelectedGpuTypes] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState<InventoryResponse | null>(null);
  const [viewingGpuType, setViewingGpuType] = useState<string | null>(null); // Track detail view mode

  // Fetch inventory from marketplace API
  const { data: inventoryData, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['marketplace-inventory', filters],
    queryFn: () => marketplaceApi.listInventory(filters),
  });

  // Fetch currency exchange rate
  const { data: currencyData } = useQuery({
    queryKey: ['currency-rates'],
    queryFn: () => currencyService.get().then((res) => res.data),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  const inventory = inventoryData?.data || [];
  const pagination = inventoryData?.pagination;

  // Filter inventory based on search query (client-side filtering for name)
  const filteredInventory = inventory.filter((item) =>
    item.gpu.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.gpu.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Convert USD to INR with GST (fallback if backend not deployed)
  const exchangeRate = currencyData?.usd_to_inr || 92; // Fetched from backend, fallback to 92
  const GST_RATE = 1.18;
  const convertToINR = (usd: number) => usd * exchangeRate * GST_RATE;

  const formatPrice = (price: number) => {
    // Handle undefined/NaN prices
    if (price === undefined || price === null || isNaN(price) || price === 0) {
      return '₹--';
    }
    return `₹${price.toFixed(2)}`;
  };

  // Get INR price with fallback to USD conversion
  // For reserved instances, hourlyUSD might be 0, so calculate from monthlyUSD
  const getINRPrice = (pricing: typeof inventory[0]['pricing']) => {
    // If hourlyINR is available and > 0, use it
    if (pricing.hourlyINR && pricing.hourlyINR > 0) {
      return pricing.hourlyINR;
    }
    // If hourlyUSD is available and > 0, convert it
    if (pricing.hourlyUSD && pricing.hourlyUSD > 0) {
      return convertToINR(pricing.hourlyUSD);
    }
    // For reserved instances, calculate hourly from monthly (730 hours/month)
    if (pricing.monthlyINR && pricing.monthlyINR > 0) {
      return pricing.monthlyINR / 730;
    }
    if (pricing.monthlyUSD && pricing.monthlyUSD > 0) {
      return convertToINR(pricing.monthlyUSD) / 730;
    }
    return 0;
  };

  const getSpotINRPrice = (pricing: typeof inventory[0]['pricing']) => {
    if (pricing.spotINR !== undefined && pricing.spotINR > 0) return pricing.spotINR;
    if (pricing.spotUSD !== undefined && pricing.spotUSD > 0) return convertToINR(pricing.spotUSD);
    return undefined;
  };

  const getAvailabilityBadge = (status: string, stock: number) => {
    switch (status) {
      case 'available':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
            Available ({stock})
          </span>
        );
      case 'limited':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
            Limited ({stock})
          </span>
        );
      case 'pre_order':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
            Pre-order
          </span>
        );
      case 'unavailable':
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-gray-400">
            Unavailable
          </span>
        );
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters({ ...filters, page: newPage });
  };

  const handleGpuTypeToggle = (type: string) => {
    const updated = selectedGpuTypes.includes(type)
      ? selectedGpuTypes.filter(t => t !== type)
      : [...selectedGpuTypes, type];
    setSelectedGpuTypes(updated);
  };

  const applyFilters = () => {
    setFilters({
      ...filters,
      gpuType: selectedGpuTypes.length > 0 ? selectedGpuTypes : undefined,
      page: 1,
    });
    setPage(1);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSelectedGpuTypes([]);
    setFilters({ page: 1, pageSize: 12 });
    setPage(1);
  };

  const handleSeeOptions = (gpuType: string) => {
    setViewingGpuType(gpuType); // Enter detail view mode
    setSelectedGpuTypes([gpuType]);
    setFilters({
      ...filters,
      gpuType: [gpuType],
      page: 1,
    });
    setPage(1);
  };

  const handleBackToMarketplace = () => {
    setViewingGpuType(null); // Exit detail view mode
    setSelectedGpuTypes([]);
    setFilters({ page: 1, pageSize: 12 });
    setPage(1);
  };

  // Count active filters
  const activeFilterCount = [
    filters.gpuType?.length,
    filters.minGpuCount,
    filters.maxGpuCount,
    filters.maxPrice,
    filters.available,
    filters.spotOnly,
  ].filter(Boolean).length;

  return (
    <div>
      <Header
        title={viewingGpuType ? `${viewingGpuType} Inventory` : "GPU Marketplace"}
        subtitle={viewingGpuType ? `All available ${viewingGpuType} configurations` : "Browse and reserve baremetal GPU servers"}
      />

      <div className="p-6 space-y-6">
        {/* Back Button - shown when viewing specific GPU type */}
        {viewingGpuType && (
          <button
            onClick={handleBackToMarketplace}
            className="flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Marketplace
          </button>
        )}

        {/* Search and Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[300px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by GPU type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-800 text-white placeholder-gray-400"
            />
          </div>
          <button
            onClick={() => setShowFilters(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium text-gray-300 transition-colors"
          >
            <FunnelIcon className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="px-1.5 py-0.5 bg-primary-500 text-white text-xs rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
          {pagination && (
            <span className="text-sm text-gray-400">
              {pagination.total} results
            </span>
          )}
        </div>

        {/* Active Filters Display - hidden when in detail view mode */}
        {!viewingGpuType && filters.gpuType && filters.gpuType.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-400">Filtered by:</span>
            {filters.gpuType.map(type => (
              <span
                key={type}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-500/20 text-primary-500 rounded-lg text-sm"
              >
                {type}
                <button
                  onClick={() => {
                    const updated = filters.gpuType?.filter(t => t !== type);
                    setSelectedGpuTypes(updated || []);
                    setFilters({
                      ...filters,
                      gpuType: updated?.length ? updated : undefined,
                      page: 1,
                    });
                  }}
                  className="p-0.5 hover:bg-primary-500/30 rounded"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
            <button
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-gray-300 underline"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Filter Modal */}
        {showFilters && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setShowFilters(false)}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                  <h3 className="text-lg font-semibold text-white">Filter GPUs</h3>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="p-1 text-gray-400 hover:text-gray-300 rounded-lg hover:bg-gray-700"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="px-6 py-4 overflow-y-auto max-h-[60vh] space-y-6">
                  {/* GPU Type Chips */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Chipset
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {GPU_TYPES.map(type => (
                        <button
                          key={type}
                          onClick={() => handleGpuTypeToggle(type)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            selectedGpuTypes.includes(type)
                              ? 'bg-primary-500 text-white'
                              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* GPU Count */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      GPU Count
                    </label>
                    <div className="flex gap-4">
                      <input
                        type="number"
                        placeholder="Min"
                        min={1}
                        className="w-24 px-3 py-2 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-400"
                        value={filters.minGpuCount || ''}
                        onChange={(e) => setFilters({ ...filters, minGpuCount: e.target.value ? Number(e.target.value) : undefined })}
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        min={1}
                        className="w-24 px-3 py-2 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-400"
                        value={filters.maxGpuCount || ''}
                        onChange={(e) => setFilters({ ...filters, maxGpuCount: e.target.value ? Number(e.target.value) : undefined })}
                      />
                    </div>
                  </div>

                  {/* Max Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Max Price (₹/hr)
                    </label>
                    <input
                      type="number"
                      placeholder="Enter max hourly rate"
                      min={0}
                      step={100}
                      className="w-full px-3 py-2 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-400"
                      value={filters.maxPrice || ''}
                      onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value ? Number(e.target.value) : undefined })}
                    />
                  </div>

                  {/* Min VRAM */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Min VRAM (GB)
                    </label>
                    <input
                      type="number"
                      placeholder="Enter minimum VRAM"
                      min={0}
                      className="w-full px-3 py-2 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-400"
                      value={filters.minVram || ''}
                      onChange={(e) => setFilters({ ...filters, minVram: e.target.value ? Number(e.target.value) : undefined })}
                    />
                  </div>

                  {/* Availability */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Availability
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.available || false}
                          onChange={(e) => setFilters({ ...filters, available: e.target.checked || undefined })}
                          className="w-4 h-4 rounded border-gray-600 text-primary-500 focus:ring-primary-500 bg-gray-800"
                        />
                        <span className="text-sm text-gray-300">Only show available</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.spotOnly || false}
                          onChange={(e) => setFilters({ ...filters, spotOnly: e.target.checked || undefined })}
                          className="w-4 h-4 rounded border-gray-600 text-primary-500 focus:ring-primary-500 bg-gray-800"
                        />
                        <span className="text-sm text-gray-300">Spot pricing available</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700 bg-gray-800">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-400 hover:text-gray-300"
                  >
                    Clear all
                  </button>
                  <button
                    onClick={applyFilters}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Inventory Grid */}
        <div className="relative">
          {/* Loading overlay for refetching */}
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-gray-900/50 z-10 pointer-events-none" />
          )}

          {isLoading ? (
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-12">
              <div className="flex flex-col items-center justify-center">
                <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4"></div>
                <p className="text-sm text-gray-400">Loading inventory...</p>
              </div>
            </div>
          ) : isError ? (
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-12">
              <div className="text-center">
                <p className="text-red-400 mb-4">Failed to load inventory</p>
                <button onClick={() => refetch()} className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600">
                  Retry
                </button>
              </div>
            </div>
          ) : filteredInventory.length === 0 ? (
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-12">
              <div className="text-center">
                <CpuChipIcon className="mx-auto h-12 w-12 text-gray-600" />
                <h3 className="mt-2 text-sm font-medium text-white">No inventory found</h3>
                <p className="mt-1 text-sm text-gray-400">
                  {searchQuery || activeFilterCount > 0 ? 'Try adjusting your search or filters.' : 'No GPU inventory is currently available.'}
                </p>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 text-sm text-primary-500 hover:text-primary-400 font-medium"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredInventory.map((item: InventoryResponse) => {
                  const displayFeatures = filterFeatures(item.features || []);
                  const spotPrice = getSpotINRPrice(item.pricing);
                  const hasSpot = typeof spotPrice === 'number' && spotPrice > 0;
                  const hasReserved = typeof item.pricing.monthlyUSD === 'number' && item.pricing.monthlyUSD > 0;

                  return (
                    <div
                      key={item.id || item.inventoryIds?.[0]}
                      className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-5 hover:border-primary-500 hover:shadow-md transition-all flex flex-col"
                    >
                      {/* Header with GPU Type Badge */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <span className="px-2.5 py-1 bg-primary-500/20 text-primary-500 rounded-md text-sm font-medium">
                          {item.gpu.type}
                        </span>
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          {item.baremetalCount && item.baremetalCount > 1 && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded-md text-xs font-medium">
                              <ServerIcon className="w-3 h-3" />
                              <span>{item.baremetalCount}</span>
                            </span>
                          )}
                          {getAvailabilityBadge(item.availability.status, item.availability.stockAvailable)}
                        </div>
                      </div>

                      {/* GPU Name */}
                      <div className="mb-3">
                        <h3 className="text-lg font-semibold text-white leading-snug">
                          {item.gpu.name}
                        </h3>
                        <p className="text-sm text-gray-400 mt-0.5">{item.gpu.manufacturer}</p>
                      </div>

                      {/* Pricing Type Labels */}
                      {(hasSpot || hasReserved) && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {hasSpot && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                              Spot Available
                            </span>
                          )}
                          {hasReserved && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs font-medium">
                              Reserved Available
                            </span>
                          )}
                        </div>
                      )}

                      {/* Features/Tags */}
                      {displayFeatures.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {displayFeatures.slice(0, 3).map((feature, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-400"
                            >
                              {feature}
                            </span>
                          ))}
                          {displayFeatures.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-400">
                              +{displayFeatures.length - 3} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Specs */}
                      <div className="grid grid-cols-4 gap-2 mb-4 py-3 border-y border-gray-700">
                        <div className="text-center">
                          <p className="text-base font-semibold text-white">{item.configuration.gpuCount}x</p>
                          <p className="text-xs text-gray-400">GPUs</p>
                        </div>
                        <div className="text-center">
                          <p className="text-base font-semibold text-white">{item.gpu.vramGB}GB</p>
                          <p className="text-xs text-gray-400">VRAM</p>
                        </div>
                        <div className="text-center">
                          <p className="text-base font-semibold text-white">{item.configuration.cpuCores}</p>
                          <p className="text-xs text-gray-400">vCPU</p>
                        </div>
                        <div className="text-center">
                          <p className="text-base font-semibold text-white">{item.configuration.ramGB}GB</p>
                          <p className="text-xs text-gray-400">RAM</p>
                        </div>
                      </div>

                      {/* Region */}
                      <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
                        <MapPinIcon className="w-4 h-4 text-primary-500" />
                        {item.region}
                      </div>

                      {/* Pricing */}
                      <div className="mb-4">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs text-gray-400 mb-0.5">Per card-hour (incl. GST)</p>
                            <p className="text-xl font-bold text-white">
                              {formatPrice(getINRPrice(item.pricing) / item.configuration.gpuCount)}
                              <span className="text-xs font-normal text-gray-400">/card-hr</span>
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {formatPrice(getINRPrice(item.pricing))}/hr total
                            </p>
                          </div>
                          {hasSpot && (
                            <div className="text-right">
                              <p className="text-xs text-gray-400 mb-0.5">Spot</p>
                              <p className="text-base font-semibold text-green-400">
                                {formatPrice(getSpotINRPrice(item.pricing)! / item.configuration.gpuCount)}
                              </p>
                              <p className="text-xs text-gray-500">/card-hr</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Stock Info */}
                      <div className="text-sm text-gray-400 mb-4">
                        {item.baremetalCount && item.baremetalCount > 0 ? (
                          <span>
                            <span className="font-medium text-gray-300">{item.baremetalCount}</span> baremetal{item.baremetalCount > 1 ? 's' : ''} available
                          </span>
                        ) : item.availability.stockAvailable > 0 ? (
                          <span>{item.availability.stockAvailable} available</span>
                        ) : (
                          <span className="text-gray-500">Currently unavailable</span>
                        )}
                      </div>

                      {/* Actions - pushed to bottom */}
                      <div className="flex gap-2 mt-auto">
                        {!viewingGpuType && (
                          <button
                            onClick={() => handleSeeOptions(item.gpu.type)}
                            className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            See Options
                          </button>
                        )}
                        <button
                          onClick={() => setSelectedItem(item)}
                          disabled={item.availability.status === 'unavailable'}
                          className={`px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors ${viewingGpuType ? 'flex-1' : 'flex-1'}`}
                        >
                          Reserve
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-300">
                    Showing page {pagination.page} of {pagination.pages} ({pagination.total} items)
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      className="px-4 py-2 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= pagination.pages}
                      className="px-4 py-2 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Reservation Modal */}
      {selectedItem && (
        <ReservationModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onSuccess={() => {
            setSelectedItem(null);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default Marketplace;
